import React, { useState, useEffect, useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import axiosInstance from '../../utils/axios';
import AuthContext from '../../context/AuthContext';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';
import { showToast } from '../reusables/CommonFunction';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Calendar } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    plugins
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

function DateRangePicker({ startDate, endDate, onStartDateChange, onEndDateChange, onPresetSelect, onPressCustomRange, activePreset }) {
    return (
        <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex gap-2">
                <Button variant={activePreset === 'today' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('today')}>Today</Button>
                <Button variant={activePreset === 'week' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('week')}>This Week</Button>
                <Button variant={activePreset === 'month' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('month')}>This Month</Button>
                <Button variant={activePreset === 'custom' ? 'secondary' : 'outline-secondary'} onClick={() => onPressCustomRange()}>Custom Range</Button>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Form.Control
                    type="date"
                    value={startDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                        onStartDateChange(new Date(e.target.value))
                    }}
                />
                <span>to</span>
                <Form.Control
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                        onEndDateChange(new Date(e.target.value))
                    }}
                />
            </div>
        </div>
    );
}

const GENDER_CHOICE = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'O', label: 'Other' }
];

const AGE_GROUPS = [
    { label: 'Child (0–12)', value: 'child' },
    { label: 'Teen (13–19)', value: 'teen' },
    { label: 'Adult (20–59)', value: 'adult' },
    { label: 'Senior (60+)', value: 'senior' }
];

const DiagnosisDashboard = () => {
    let { userDetails } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [initialStartDate, setInitialStartDate] = useState(new Date());
    const [initialEndDate, setInitialEndDate] = useState(new Date());
    const [diagnosisData, setDiagnosisData] = useState({
        barChart: [],
        pieChart: []
    });

    const [activePreset, setActivePreset] = useState('month');
    const [selectedGenders, setSelectedGenders] = useState(GENDER_CHOICE.map(g => g.value));
    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

    const onPressCustomRange = () => {
        if (!startDate || !endDate) {
            showToast("error", "Start date and End date cannot be empty!");
            return;
        }
        if (startDate > endDate) {
            showToast("error", "Start date cannot be greater than End date!");
            return;
        }
        if (endDate < startDate) {
            showToast("error", "End date cannot be less than Start date!");
            return;
        }
        if (startDate === initialStartDate && endDate === initialEndDate) {
            showToast("info", "Please modify Start Date or End Date before submitting.");
            setActivePreset(activePreset);
            return;
        }

        setActivePreset('custom');
        fetchDiagnosisData(startDate, endDate);
    }

    const handlePresetSelect = (preset) => {
        const today = new Date();
        let start = new Date();
        let end = new Date();

        switch (preset) {
            case 'today':
                start = new Date(today);
                start.setDate(today.getDate());

                end = new Date(today);
                end.setDate(today.getDate());
                break;
            case 'week':
                start = new Date(today);
                start.setDate(today.getDate() - 6);

                end = new Date(today);
                end.setDate(today.getDate());
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth(), 2);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                break;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
        setInitialStartDate(start);
        setInitialEndDate(end);
        setActivePreset(preset);
        fetchDiagnosisData(start, end);
    };

    const fetchDiagnosisData = (start, end) => {
        try {
            setLoading(true);
            axiosInstance
                .get('/patients/diagnosis_dashboard/', {
                    params: {
                        clinicId: userDetails.clinic_id,
                        start_date: start.toISOString().split('T')[0],
                        end_date: end.toISOString().split('T')[0],
                        age_group: selectedAgeGroup || null,
                        gender: selectedGenders.length > 0 ? selectedGenders : null,
                    }
                })
                .then(response => {
                    if (response.status === 200) {
                        const data = response.data;
                        setDiagnosisData({
                            barChart: data.bar_chart,
                            pieChart: data.pie_chart
                        });
                    }
                })
                .catch(error => {
                    if (error.response) {
                        showToast("error", error.response.data.message);
                    } else {
                        showToast("error", "An error occurred while fetching data.");
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            setLoading(false);
            showToast("error", "An error occurred while fetching data.");
        }
    };

    // Helper: Wrap label text after a certain number of characters
    const wrapLabel = (label, maxChar = 25) => {
        if (!label) return '';
        const regex = new RegExp(`(.{1,${maxChar}})(\\s|$)`, 'g');
        return label.match(regex)?.join('\n') || label;
    };

    // Helper: Truncate label and add ellipsis
    const truncateLabel = (label, maxLength = 35) => {
        return label.length > maxLength ? label.slice(0, maxLength) + '...' : label;
    };

    const rawLabels = diagnosisData.barChart.map(item => item.diagnosis);
    const barChartData = {
        labels: rawLabels.map(label => wrapLabel(truncateLabel(label, 35), 25)),
        datasets: [
            {
                data: diagnosisData.barChart.map(item => item.count),
                backgroundColor: 'rgba(40, 167, 69, 0.6)',
                borderColor: 'rgb(40, 167, 69)',
                borderWidth: 1
            }
        ]
    };

    const barChartOptions = {
        indexAxis: 'y', // Horizontal mode
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    title: function (tooltipItems) {
                        const index = tooltipItems[0].dataIndex;
                        return rawLabels[index]; // Show full label in tooltip
                    }
                }
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Number of Cases',
                },
                ticks: {
                    precision: 0,
                    callback: (value) => Number(value),
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Diagnosis',
                },
                ticks: {
                    callback: function (value, index) {
                        return barChartData.labels[index];
                    }
                }
            }
        }
    };

    const fullDiagnosisData = {
        labels: diagnosisData.pieChart.map(item => item.diagnosis),
        datasets: [{
            label: 'Diagnosis Count',
            data: diagnosisData.pieChart.map(item => item.count),
            backgroundColor: 'rgba(54, 162, 235, 0.6)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
        }]
    };

    const fullDiagnosisOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'x',
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        scales: {
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 60,
                    minRotation: 45,
                    callback: function (value, index) {
                        const label = fullDiagnosisData.labels[index];
                        return label.length > 30 ? label.slice(0, 30) + '…' : label;
                    }
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Number of Cases',
                },
                ticks: {
                    precision: 0
                }
            }
        }
    };

    useEffect(() => {
        // Set initial date range to current month
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 2);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        setStartDate(start);
        setEndDate(end);
        setInitialStartDate(start);
        setInitialEndDate(end);
    }, []);

    useEffect(() => {
        if (startDate && endDate && userDetails?.clinic_id) {
            fetchDiagnosisData(startDate, endDate);
        }
    }, [startDate, endDate, selectedGenders, selectedAgeGroup, userDetails]);

    return (
        <>
            <Row className='ml-3 mr-3 mb-3'>
                <div className='col-md-12'>
                    <Card className="mb-4">
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Calendar size={20} className="text-muted" />
                                <h5 className="mb-0">Select Date Range</h5>
                            </div>
                            <Row className='mb-3'>
                                <Col xs={12}>
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onStartDateChange={setStartDate}
                                        onEndDateChange={setEndDate}
                                        onPresetSelect={handlePresetSelect}
                                        onPressCustomRange={onPressCustomRange}
                                        activePreset={activePreset}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Gender</Form.Label>
                                        <div className="d-flex gap-3">
                                            {GENDER_CHOICE.map(({ value, label }) => (
                                                <Form.Check
                                                    key={value}
                                                    type="checkbox"
                                                    id={`gender-${value}`}
                                                    label={label}
                                                    value={value}
                                                    checked={selectedGenders.includes(value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedGenders([...selectedGenders, value]);
                                                        } else {
                                                            setSelectedGenders(prevValues => prevValues.filter(selectedGenders => selectedGenders !== value));
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Age Group</Form.Label>
                                        <Form.Select
                                            value={selectedAgeGroup || ''}
                                            onChange={(e) => {
                                                setSelectedAgeGroup(e.target.value)
                                            }}>
                                            <option value="">All Age Groups</option>
                                            {AGE_GROUPS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="mb-4">
                        <Col lg={12}>
                            <Card>
                                <Card.Body>
                                    <h5>Top 5 Diagnoses</h5>
                                    <div style={{ height: '400px', overflowX: 'auto' }} className="d-flex justify-content-center">
                                        <Bar data={barChartData} options={barChartOptions} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col lg={12}>
                            <Card>
                                <Card.Body>
                                    <h5>Full Diagnosis Distribution</h5>
                                    <div style={{ height: '400px', overflowX: 'auto' }} className="d-flex justify-content-center">
                                        <div style={{ width: Math.max(diagnosisData.pieChart.length * 80, 600) }}>
                                            <Bar data={fullDiagnosisData} options={fullDiagnosisOptions} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Row>
            <ToastContainer />
            <BusyLoaderOverlay show={loading} />
        </>
    );
}

export default DiagnosisDashboard;